import { SerializedStyles, css, useTheme } from "@emotion/react"
import React, { ReactNode } from "react"
import { Link } from "@gatsbyjs/reach-router";

import { UpsunTheme } from "../themes"

import { pxToRem } from "../styles"
import { getFontWeight, mediaQueries } from "../styles/css-utils"

import { 
  HighlightStrongBorderStrong,
  NeutralBgDisabled,
  NeutralBgInvDefault,
  NeutralBorderStrongDefault,
  NeutralBorderWeakDisabled,
  NeutralTxtInvDefault,
  NeutralTxtInvDisabled,
  NeutralTxtStrongDefault,
  PrimaryBgActive,
  PrimaryBgDefault,
  PrimaryBgHover,
  PrimaryBgWeakHover,
  PrimaryTxtInvDefault,
  NeutralTxtDisabled,
  NeutralBorderWeakActive
} from "../../tokens/js/light"

import {
  PrimaryBgHover as PrimaryBgHoverDark,
  PrimaryBgActive as PrimaryBgActiveDark,
  NeutralBorderStrongDefault as NeutralBorderStrongDefaultDark,
  PrimaryTxtInvDefault as PrimaryTxtInvDefaultDark,
  NeutralTxtStrongDefault as NeutralTxtStrongDefaultDark,
  HighlightStrongBorderStrong as HighlightStrongBorderStrongDark,
  NeutralBgInvDefault as NeutralBgInvDefaultDark,
  NeutralTxtInvDefault as NeutralTxtInvDefaultDark,
  PrimaryBgWeakHover as PrimaryBgWeakHoverDark,
  NeutralBgDisabled as NeutralBgDisabledDark,
  NeutralTxtInvDisabled as NeutralTxtInvDisabledDark,
  NeutralBorderWeakDisabled as NeutralBorderWeakDisabledDark,
  NeutralTxtDisabled as NeutralTxtDisabledDark,
  NeutralBorderWeakActive as NeutralBorderWeakActiveDark
} from "../../tokens/js/dark"

import {
  ComponentButtonPaddingBottom,
  ComponentButtonPaddingLeft,
  ComponentButtonPaddingRight,
  ComponentButtonPaddingTop,
  MobileButtonFontFamily,
  MobileButtonFontSize,
  MobileButtonFontWeight,
  RadiusXxl,
} from "../../tokens/js/mobile"

import {
  ComponentButtonPaddingBottom as PaddingBottomTablet,
  ComponentButtonPaddingLeft as PaddingLeftTablet,
  ComponentButtonPaddingRight as PaddingRightTablet,
  ComponentButtonPaddingTop as PaddingTopTablet,
  RadiusXxl as BorderRadiusTablet,
  TabletBodyFontSize
} from "../../tokens/js/tablet"

import {
  ComponentButtonPaddingBottom as PaddingBottomDesktop,
  ComponentButtonPaddingLeft as PaddingLeftDesktop,
  ComponentButtonPaddingRight as PaddingRightDesktop,
  ComponentButtonPaddingTop as PaddingTopDesktop,
  RadiusXxl as BorderRadiusDesktop,
  DesktopBodyFontSize
} from "../../tokens/js/desktop"

type ButtonProps = {
  text?: string
  shortText?: string
  href?: string
  level?: "primary" | "secondary" | "tertiary"
  children?: ReactNode
  css?: SerializedStyles
  disabled?: boolean
  type?: string
  download?: boolean
  onClick?: () => void
}
// @todo Add a prop for icons
const Button: React.FC<ButtonProps> = ({
  text,
  shortText,
  href,
  level,
  children,
  disabled,
  type,
  download,
  ...props
}) => {
  const levelUsed = level ? level : "primary"
  const isExternalOrAnchorLink = (href?.substring(0, 4) === "http" || href?.substring(0,1) === "#");
  const hasShortText = shortText && shortText.length > 0;

  const theme = useTheme() as UpsunTheme

  const baseBtnStyles = css`
    position: relative;
    display: inline-flex;
    justify-content: center;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    font-family: ${MobileButtonFontFamily};
    font-size: ${pxToRem(MobileButtonFontSize)};
    font-weight: ${getFontWeight(MobileButtonFontWeight)};
    line-height: 1;
    outline-offset: ${pxToRem(2)};
    cursor: pointer;

    ${mediaQueries.md} {
      font-size: ${pxToRem(TabletBodyFontSize)};
    }

    ${mediaQueries.lg} {
      font-size: ${pxToRem(DesktopBodyFontSize)};
    }

    &[disabled] {
      cursor: auto;
    }
  `

  const primaryStyles = css`
    background: ${PrimaryBgDefault};
    border: ${pxToRem(2)} solid ${PrimaryBgDefault};
    border-radius: ${pxToRem(RadiusXxl)};
    padding: ${pxToRem(ComponentButtonPaddingTop)} ${pxToRem(ComponentButtonPaddingRight)} ${pxToRem(ComponentButtonPaddingBottom)} ${pxToRem(ComponentButtonPaddingLeft)};
    color: ${theme.name === "light" ? PrimaryTxtInvDefault : PrimaryTxtInvDefaultDark};

    &:hover {
      background: ${theme.name === "light" ? PrimaryBgHover : PrimaryBgHoverDark};
      border-color: ${theme.name === "light" ? PrimaryBgHover : PrimaryBgHoverDark};
    }

    &:active {
      background: ${theme.name === "light" ? PrimaryBgActive : PrimaryBgActiveDark };
      border-color: ${theme.name === "light" ? PrimaryBgActive : PrimaryBgActiveDark };
    }

    &:focus {
      outline: ${pxToRem(2)} solid ${theme.name === "light" ? HighlightStrongBorderStrong : HighlightStrongBorderStrongDark};
    }

    &[disabled] {
      background: ${theme.name === "light" ? NeutralBgDisabled : NeutralBgDisabledDark};
      color: ${theme.name === "light" ? NeutralTxtInvDisabled : NeutralTxtInvDisabledDark};
      border-color: ${theme.name === "light" ? NeutralBgDisabled : NeutralBgDisabledDark};
    }

    ${mediaQueries.md} {
      padding: ${pxToRem(PaddingTopTablet)} ${pxToRem(PaddingRightTablet)} ${pxToRem(PaddingBottomTablet)} ${pxToRem(PaddingLeftTablet)};
      border-radius: ${pxToRem(BorderRadiusTablet)};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(PaddingTopDesktop)} ${pxToRem(PaddingRightDesktop)} ${pxToRem(PaddingBottomDesktop)} ${pxToRem(PaddingLeftDesktop)};
      border-radius: ${pxToRem(BorderRadiusDesktop)};
    }
  `

  const secondaryStyles = css`
    border: ${pxToRem(2)} solid ${theme.name === "light" ? NeutralBorderStrongDefault : NeutralBorderStrongDefaultDark };
    background: transparent;
    border-radius: ${pxToRem(RadiusXxl)};
    padding: ${pxToRem(ComponentButtonPaddingTop)} ${pxToRem(ComponentButtonPaddingRight)} ${pxToRem(ComponentButtonPaddingBottom)} ${pxToRem(ComponentButtonPaddingLeft)};
    color: ${theme.name === "light" ? NeutralTxtStrongDefault : NeutralTxtStrongDefaultDark };

    &:hover, &:active {
      color: ${theme.name === "light" ? NeutralTxtInvDefault : NeutralTxtInvDefaultDark};
      background: ${theme.name === "light" ? NeutralBgInvDefault : NeutralBgInvDefaultDark};
    }

    &:focus {
      outline: ${pxToRem(2)} solid ${theme.name === "light" ? HighlightStrongBorderStrong : HighlightStrongBorderStrongDark};
      border-color: ${theme.name === "light" ? NeutralBorderWeakActive : NeutralBorderWeakActiveDark}
    }

    &[disabled] {
      background: transparent;
      color: ${theme.name === "light" ? NeutralTxtDisabled : NeutralTxtDisabledDark};
      border-color: ${theme.name === "light" ? NeutralBorderWeakDisabled : NeutralBorderWeakDisabledDark};
    }

    ${mediaQueries.md} {
      padding: ${pxToRem(PaddingTopTablet)} ${pxToRem(PaddingRightTablet)} ${pxToRem(PaddingBottomTablet)} ${pxToRem(PaddingLeftTablet)};
      border-radius: ${pxToRem(BorderRadiusTablet)};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(PaddingTopDesktop)} ${pxToRem(PaddingRightDesktop)} ${pxToRem(PaddingBottomDesktop)} ${pxToRem(PaddingLeftDesktop)};
      border-radius: ${pxToRem(BorderRadiusDesktop)};
    }
  `

  const tertiaryStyles = css`
    color: ${theme.name === "light" ? NeutralTxtStrongDefault : NeutralTxtStrongDefaultDark };
    border: none;
    border-bottom: ${pxToRem(2)} solid ${theme.name === "light" ? NeutralBorderStrongDefault : NeutralBorderStrongDefaultDark };
    padding-top: ${pxToRem(4)};
    padding-bottom: ${pxToRem(4)};
    background: transparent;

    &:hover, &:active {
      color: ${theme.name === "light" ? NeutralTxtInvDefault : NeutralTxtStrongDefaultDark};
      background: ${theme.name === "light" ? PrimaryBgWeakHover : PrimaryBgWeakHoverDark};
    }

    &:focus {
      outline: ${pxToRem(2)} solid ${theme.name === "light" ? HighlightStrongBorderStrong : HighlightStrongBorderStrongDark};
    }

    &[disabled] {
      background: transparent;
      color: ${theme.name === "light" ? NeutralTxtDisabled : NeutralTxtDisabledDark};
      border-color: ${theme.name === "light" ? NeutralBorderWeakDisabled : NeutralBorderWeakDisabledDark};
    }
  `

  const combinedBtnStyles = [baseBtnStyles]

  switch (levelUsed) {
    case "tertiary":
      combinedBtnStyles.push(tertiaryStyles)
      break
    case "secondary":
      combinedBtnStyles.push(secondaryStyles)
      break
    case "primary":
    default:
      combinedBtnStyles.push(primaryStyles)
  }

  const shortTextStyles = css`
    ${mediaQueries.md} {
      display: none;
    }
  `;

  const fullTextStyles = css`
    display: none;

    ${mediaQueries.md} {
      display: inline;
    }
  `;

  if (type === "submit") {
    return(
      <input type="submit" css={combinedBtnStyles} value={text || "Submit"} />
    )
  } else if (!href) {
    return (
      <button {...props} css={combinedBtnStyles} disabled={disabled}>
        { children && children }
        { (!children && !hasShortText) && text }
        { (!children && hasShortText) && <>
          <span css={fullTextStyles}>{text}</span><span css={shortTextStyles}>{shortText}</span>
        </>}
      </button>
    )
  } else if ((href && isExternalOrAnchorLink) || download) {
    // HTML is still too silly to understand `download="false"`
    let moddableProps: any = {...props};
    if (download) {
      moddableProps.download = true;
    }

    return (
      <a {...moddableProps} href={href} css={combinedBtnStyles}>
        { children && children }
        { (!children && !hasShortText) && text }
        { (!children && hasShortText) && <>
          <span css={fullTextStyles}>{text}</span><span css={shortTextStyles}>{shortText}</span>
        </>}
      </a>
    )
  } else {
    return (
      <Link {...props} to={href} css={combinedBtnStyles}>
        { children && children }
        { (!children && !hasShortText) && text }
        { (!children && hasShortText) && <>
          <span css={fullTextStyles}>{text}</span><span css={shortTextStyles}>{shortText}</span>
        </>}
      </Link>
    )
  }
}

export default Button
